import React, { useCallback, useContext, useMemo } from 'react'
import { Drawer, ThemeProvider } from '@material-ui/core'
import { UpdateUserPreferencesDTO } from './types/preferences'
import { SettingsContext } from './SettingsProvider'
import { SettingsForm } from './SettingsForm'
import themeV2 from '../../theme-v2'
import CloseButton from '../../components/Buttons/CloseButton'
import { UserPreferencesContext } from '../../contexts/preferences/UserPreferencesContext'
import { SIDEBAR_NAVIGATION_WIDTH } from '../../components/Navigation/constants'
import { useSetUserPreferencesMutation } from '../../graphql/api/queries/SetUserPreferences.generated'
import i18n from '../../i18n'
import { DatePattern } from '../../constants/preferences'

const SettingsSidebar = () => {
  const profile = useContext(UserPreferencesContext)
  const { open, toggleOpen } = useContext(SettingsContext)

  const onClose = useCallback(() => {
    toggleOpen()
  }, [toggleOpen])

  const [setUserPreferences, { loading }] = useSetUserPreferencesMutation({
    onCompleted: async ({ setUserPreferences }) => {
      const language = setUserPreferences.preferences?.language || 'en'
      await i18n.changeLanguage(language)
      onClose()
    },
    onError: (error) => {
      console.error('Failed to set user preferences:', error)
    },
  })

  const initial = useMemo((): UpdateUserPreferencesDTO => {
    return {
      areaUnit: profile.area.name,
      defaultCountryId: profile.country.id,
      defaultRegionId: profile.region.id,
      lengthUnit: profile.length.name,
      massUnit: profile.weight.name,
      volumeUnit: profile.volume.name,
      language: profile.language || 'en',
      datePattern: profile.datePattern as DatePattern,
    }
  }, [profile])

  const onSettingsSaved = useCallback(
    async (dto: UpdateUserPreferencesDTO) => {
      try {
        await setUserPreferences({
          variables: {
            userId: profile.userId,
            preferences: {
              countryId: dto.defaultCountryId,
              regionId: dto.defaultRegionId,
              areaUnit: dto.areaUnit,
              datePattern: dto.datePattern,
              language: dto.language,
              lengthUnit: dto.lengthUnit,
              massUnit: dto.massUnit,
              volumeUnit: dto.volumeUnit,
              showContactDetails: true,
            },
          },
        })
        window.location.reload()
      } catch (error) {
        console.error('Error saving settings:', error)
      }
    },
    [profile.userId, setUserPreferences],
  )

  const onFail = useCallback((errors: any) => {
    console.error('Validation failures', errors)
  }, [])

  return (
    <ThemeProvider theme={themeV2}>
      <Drawer
        open={open}
        style={{ marginLeft: SIDEBAR_NAVIGATION_WIDTH, zIndex: 70 }}
        BackdropProps={{ style: { marginLeft: SIDEBAR_NAVIGATION_WIDTH, zIndex: 10 } }}
        PaperProps={{ style: { marginLeft: SIDEBAR_NAVIGATION_WIDTH, zIndex: 10 } }}
        onClose={onClose}
      >
        <CloseButton onClick={onClose} />
        <SettingsForm initial={initial} loading={loading} onSave={onSettingsSaved} onFail={onFail} />
      </Drawer>
    </ThemeProvider>
  )
}

export default SettingsSidebar
